import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Btn, Input, Select, Modal, Card, Badge, EmptyState, fmtDate } from "@/components/ui-kit";
import { Plus, AlertTriangle } from "lucide-react";

export default function Inventory() {
  const { t } = useLang();
  const { user } = useAuth();
  const [txns, setTxns] = useState([]);
  const [products, setProducts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ product_id: "", txn_type: "incoming", quantity: "", batch_number: "", expiry_date: "", reference: "", notes: "" });

  const load = async () => {
    const [t, p, a] = await Promise.all([api.get("/inventory/transactions"), api.get("/products"), api.get("/inventory/expiry-alerts")]);
    setTxns(t.data); setProducts(p.data); setAlerts(a.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/inventory/transactions", { ...form, quantity: Number(form.quantity) });
    setOpen(false); setForm({ product_id: "", txn_type: "incoming", quantity: "", batch_number: "", expiry_date: "", reference: "", notes: "" });
    await load();
  };

  const typeColor = (t) => t === "incoming" ? "green" : t === "outgoing" ? "blue" : t === "damaged" ? "red" : t === "returned" ? "amber" : "slate";

  return (
    <div data-testid="inventory-page">
      <PageHeader
        title={t("inventory")}
        subtitle="Stock movement & expiry monitoring"
        actions={(user.role === "admin" || user.role === "supervisor") && (
          <Btn onClick={() => setOpen(true)} data-testid="add-txn-btn"><Plus className="w-4 h-4"/>Transaction</Btn>
        )}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {products.map(p => (
          <Card key={p.id} className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded grid place-items-center text-white text-xs font-bold" style={{ backgroundColor: p.image_color }}>
                {p.name.slice(0,2).toUpperCase()}
              </div>
              <div className="flex-1">
                <div className="font-semibold">{p.name}</div>
                <div className="text-xs text-slate-500">{p.batches?.length || 0} batches</div>
              </div>
              <div className="text-end">
                <div className={`text-2xl font-extrabold ${p.current_stock < 1000 ? "text-amber-600" : "text-blue-700"}`}>{p.current_stock}</div>
                <div className="text-[10px] uppercase text-slate-500 font-bold">units</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {alerts.length > 0 && (
        <Card className="p-4 mb-6 border-amber-300 bg-amber-50">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-700"/>
            <h3 className="font-semibold text-amber-900">Expiry Alerts (next 90 days)</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {alerts.map((a, i) => (
              <div key={i} className="bg-white rounded p-2 text-sm border border-amber-200">
                <div className="font-semibold">{a.product_name}</div>
                <div className="text-xs text-slate-600">Batch: {a.batch_number} · Exp: {a.expiry_date} · Qty: {a.quantity}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <div className="px-5 pt-5 pb-3 border-b border-slate-100">
          <h3 className="text-lg font-semibold" style={{ fontFamily: "Manrope" }}>Transactions</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-y text-xs uppercase tracking-wider text-slate-500">
                <th className="py-2 px-3 text-start">Date</th>
                <th className="py-2 px-3 text-start">Product</th>
                <th className="py-2 px-3 text-start">Type</th>
                <th className="py-2 px-3 text-end">Quantity</th>
                <th className="py-2 px-3 text-start">Batch</th>
                <th className="py-2 px-3 text-start">Reference</th>
                <th className="py-2 px-3 text-start">User</th>
              </tr>
            </thead>
            <tbody>
              {txns.map(x => (
                <tr key={x.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 px-3 text-xs">{fmtDate(x.created_at)}</td>
                  <td className="py-2 px-3 font-semibold">{x.product_name}</td>
                  <td className="py-2 px-3"><Badge color={typeColor(x.txn_type)}>{x.txn_type}</Badge></td>
                  <td className="py-2 px-3 text-end font-bold">{x.quantity}</td>
                  <td className="py-2 px-3 font-mono text-xs">{x.batch_number || "-"}</td>
                  <td className="py-2 px-3 text-xs">{x.reference || "-"}</td>
                  <td className="py-2 px-3 text-xs">{x.user_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {txns.length === 0 && <EmptyState message={t("noData")}/>}
        </div>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title="Inventory Transaction">
        <form onSubmit={submit} className="space-y-3">
          <Select label="Product *" required value={form.product_id} onChange={(e) => setForm({...form, product_id: e.target.value})}>
            <option value="">--</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Select label="Type *" value={form.txn_type} onChange={(e) => setForm({...form, txn_type: e.target.value})}>
            <option value="incoming">Incoming</option><option value="outgoing">Outgoing</option><option value="damaged">Damaged</option><option value="returned">Returned</option><option value="adjustment">Adjustment</option>
          </Select>
          <Input label="Quantity *" type="number" required value={form.quantity} onChange={(e) => setForm({...form, quantity: e.target.value})}/>
          <Input label="Batch Number" value={form.batch_number} onChange={(e) => setForm({...form, batch_number: e.target.value})}/>
          <Input label="Reference" value={form.reference} onChange={(e) => setForm({...form, reference: e.target.value})}/>
          <Input label="Notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Btn variant="ghost" type="button" onClick={() => setOpen(false)}>{t("cancel")}</Btn>
            <Btn type="submit">{t("save")}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
